import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import CategoryNavbar from "./CategoryNavbar";
import vyapakprintsLogo from "../assets/vyapakprints-logo.png";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSticky, setIsSticky] = useState(false);

  const { cartItems } = useContext(CartContext);
  const navigate = useNavigate();
  const accountRef = useRef(null);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/search?query=${encodeURIComponent(q)}`);
      setSearchQuery("");
      setIsMenuOpen(false);
      setIsAccountOpen(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setIsAccountOpen(false);
      }
    }

    function handleScroll() {
      setIsSticky(window.scrollY > 80);
    }

    function handleEsc(e) {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        setIsAccountOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  return (
    <header
      className={`w-full z-[9999] ${
        isSticky ? "sticky top-0 bg-white shadow-md" : "bg-white"
      }`}
    >
      <nav className="text-mainpink border-b border-pink-100 relative z-[9999] bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center shrink-0">
              <img
                src={vyapakprintsLogo}
                className="w-32 sm:w-40 md:w-48"
                alt="Vyapakprints"
              />
            </Link>

            <div className="hidden md:block w-full max-w-xl">
              <form onSubmit={handleSearch} className="flex w-full">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 rounded-l-md bg-pink-100 text-mainpink placeholder-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-300 transition"
                  aria-label="Search products"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-mainpink text-white rounded-r-md hover:bg-pink-700 transition flex items-center justify-center"
                  aria-label="Submit search"
                >
                  <i className="fa-solid fa-magnifying-glass" />
                </button>
              </form>
            </div>

            <div className="flex items-center gap-4 sm:gap-6">
              <div className="relative" ref={accountRef}>
                <button
                  type="button"
                  onClick={() => setIsAccountOpen((prev) => !prev)}
                  className="hover:text-pink-700 transition flex flex-col items-center focus:outline-none"
                  aria-expanded={isAccountOpen}
                  aria-label="Account options"
                >
                  <div className="flex items-center gap-1">
                    <i className="fa-solid fa-user text-lg sm:text-xl" />
                    <i className="fa-solid fa-chevron-down text-[10px]" />
                  </div>
                  <span className="text-[10px] sm:text-xs mt-1">Account</span>
                </button>

                <div
                  className={`absolute right-0 top-full mt-2 w-56 z-[9999] rounded-md border border-pink-100 bg-white shadow-lg overflow-hidden transition-all duration-200 ${
                    isAccountOpen
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-1"
                  }`}
                >
                  <Link
                    to="/account"
                    onClick={() => setIsAccountOpen(false)}
                    className="block px-4 py-3 text-sm text-mainpink hover:bg-pink-50"
                  >
                    Login
                  </Link>

                  <a
                    href="https://business.vyapakprint.com/login"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsAccountOpen(false)}
                    className="block px-4 py-3 text-sm text-mainpink hover:bg-pink-50"
                  >
                    B2B Login
                  </a>

                  <a
                    href="https://admin.vyapakprint.com/login"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsAccountOpen(false)}
                    className="block px-4 py-3 text-sm text-mainpink hover:bg-pink-50"
                  >
                    Admin Login
                  </a>

                  <a
                    href="https://vyapakprint.com/applydistributionship"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsAccountOpen(false)}
                    className="block px-4 py-3 text-sm text-mainpink hover:bg-pink-50 border-t border-pink-100"
                  >
                    Apply for Distributorship
                  </a>
                </div>
              </div>

              <Link
                to="/cart"
                className="hover:text-pink-700 transition flex flex-col items-center"
              >
                <span className="relative">
                  <i className="fa-solid fa-bag-shopping text-lg sm:text-xl" />
                  {cartItems.length > 0 && (
                    <span className="absolute -top-2 -right-2 rounded-full bg-red-500 text-white text-[10px] w-5 h-5 flex items-center justify-center">
                      {cartItems.length}
                    </span>
                  )}
                </span>
                <span className="text-[10px] sm:text-xs mt-1">Cart</span>
              </Link>

              <button
                className="md:hidden text-2xl focus:outline-none hover:text-pink-700 transition"
                onClick={() => setIsMenuOpen((prev) => !prev)}
                aria-expanded={isMenuOpen}
                aria-label="Toggle navigation menu"
              >
                <i
                  className={`fa-solid ${isMenuOpen ? "fa-xmark" : "fa-bars"}`}
                />
              </button>
            </div>
          </div>

          <div className="md:hidden mt-3">
            <form onSubmit={handleSearch} className="flex w-full">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 rounded-l-md bg-pink-100 text-mainpink placeholder-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-300 transition"
                aria-label="Search products"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-mainpink text-white rounded-r-md hover:bg-pink-700 transition"
                aria-label="Submit search"
              >
                <i className="fa-solid fa-magnifying-glass" />
              </button>
            </form>
          </div>
        </div>

        <div
          className={`${isMenuOpen ? "block" : "hidden"} md:block relative z-30`}
        >
          <CategoryNavbar closeMenu={() => setIsMenuOpen(false)} />
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
